import { ProductParams } from "@/api/home";
import { EResourceCardType, getProductFeedList } from "@/api/home/feedList";

import ProductWaterfall from "@/components/product-waterfall";
import { getCookie } from "@/utils/platform";
import { ProductFeedListCardModel, ProductListItemModel, TProductFeedItem } from "@akc/biz-api";
import {
  ResourceFeedBannerComp,
  ResourceFeedLiveComp,
  ResourceFeedRankComp,
} from "@akc/mx-taro-h5-components";
// import useNavigation from "@/utils/useNavigate";
import React, { useEffect, useRef, useState } from "react";
import styles from "./ProductFeedList.module.less";

type Props = {
  // 页 面code
  pageId: string;
  // 类目code
  activityTagNo: string;
  activityTagIndex?: number;
  activityTagName?: string;
  previewRsc?: string;
  previousPageName?: string;
};

/**
 * 商品feed流组件
 * 支持在商品前后插入资源位卡片
 */
const ProductFeedList = ({
  pageId,
  activityTagNo,
  activityTagIndex = 0,
  activityTagName,
  previewRsc,
  previousPageName,
}: Props): React.ReactNode => {
  const [feedList, setFeedList] = useState<Array<TProductFeedItem>>([]);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const productParamsRef = useRef<ProductParams>();
  const clickedSpuId = useRef<string[]>([]);

  // 卡片在feed流中的总排序,从1开始
  const totalIndex = useRef<number>(1);
  // 获取导航函数
  // const { navigate2H5 } = useNavigation();

  // 搜索请求id不变
  const searchRequestId = useRef<string>((getCookie("msUserCode") ?? "userId") + Date.now());

  /**
   * 加载一页商品
   */
  const loadOnePageFeedList = async () => {
    if (!productParamsRef.current) {
      return;
    }
    try {
      const rsp: any = await getProductFeedList({
        ...productParamsRef.current,
        searchRequestId: searchRequestId.current,
      });
      // 处理数据，将商品和资源位卡片一起展示
      setHasNext(rsp.hasNext);
      if (rsp.hasNext) {
        productParamsRef.current.pageIndex++;
      }
      // eslint-disable-next-line no-underscore-dangle
      let _result: Array<TProductFeedItem> = [];
      if (rsp?.result?.length > 0) {
        // 更新下一页页码和最后一个商品id
        const lastProduct = rsp.result[rsp.result.length - 1];
        // 过滤纯商品的长度（排除有cardType的插卡）
        let feedProductListLength = feedList.filter(item => !("cardType" in item)).length;
        productParamsRef.current.lastActivitySpuCode = lastProduct?.activitySpuId || "";

        // 处理插卡
        rsp.result.forEach((item: ProductListItemModel, index: number) => {
          // 后置插卡暂存列表
          const afterSrcCardList: Array<ProductFeedListCardModel> = [];
          // 加插卡
          if (
            Array.isArray(item.productFeedListCardList) &&
            item.productFeedListCardList?.length > 0
          ) {
            item.productFeedListCardList.forEach(sourceCardItem => {
              // 插卡类型 1-上方插入  2-之后插入（默认）
              // 后置插件先暂时保存，查完商卡再插入，前置插件直接插入，
              if (sourceCardItem.cardLocationType !== 1) {
                afterSrcCardList.push(sourceCardItem);
              } else {
                _result.push(sourceCardItem);
              }
            });
          }
          // 加商品本身
          // _result.push(item);
          _result.push({
            ...item,
            rank: feedProductListLength + index + 1,
          });

          // 插入后置插卡
          if (afterSrcCardList.length) {
            _result.push(...afterSrcCardList);
          }
        });

        // 为商品和插卡映射总排序
        _result = _result.map(item => {
          totalIndex.current++;
          // 判断是否为商品卡片（ProductListItemModel）而非资源位卡片
          const isProductItem = !("cardType" in item);
          return {
            ...item,
            rank: totalIndex.current,
            // title: `${item?.resource_rank}-${item?.title}`,
            trackData: {
              recommend_requestid: searchRequestId.current,
              resource_rank: item?.rank,
              export_tracking_plan_id: "14444",
              tracking_plan_id: "14445",
              // 只有商品卡片才有这些属性
              click_activity_spu_code: isProductItem
                ? (item as ProductListItemModel)?.activitySpuId
                : undefined,
              click_activity_id: isProductItem
                ? (item as ProductListItemModel)?.activityId
                : undefined,
              click_sku_id: isProductItem ? (item as ProductListItemModel)?.selectSkuId : undefined,
            },
          };
        });

        setFeedList(feedList => {
          return [...(feedList || []), ..._result];
        });
      }
      return Promise.resolve();
    } catch (error) {
      console.error("获取商品feed流失败", error);
      return Promise.reject(error);
    }
  };

  /**
   * 渲染资源位卡片
   */
  const resourceCardRender = (cardItem: TProductFeedItem, index: number) => {
    // 类型首位，判断如果是普通商品卡片（ProductListItemModel类型），则无需自定义渲染
    if (!("cardType" in cardItem)) {
      return null;
    }

    switch (cardItem?.cardType) {
      // feed流banner资源位
      case EResourceCardType.BANNER:
        return (
          <div className={styles.ResourceFeedBannerCompWrap}>
            <ResourceFeedBannerComp
              data={cardItem.bannerCards}
              trackData={{
                page_name: "首页",
                previous_page_name: previousPageName,
                tab_name: activityTagName,
                tab_rank: activityTagIndex + 1,
                resource_type: "banner",
                comp_name: "商品feed流",
                comp_type: "feed流",
                resource_rank: cardItem.rank,
                comp_index: "",
                relative_rank: index + 1,
              }}
            />
          </div>
        );
      // feed流直播资源位
      case EResourceCardType.LIVE:
        return (
          <div className={styles.ResourceFeedLiveCompWrap}>
            <ResourceFeedLiveComp
              data={cardItem.liveCardVO}
              trackData={{
                page_name: "首页",
                previous_page_name: previousPageName,
                resource_type: "直播",
                resource_content: "直播",
                resource_rank: cardItem.rank,
              }}
            />
          </div>
        );
      // feed流纵向榜单资源位
      case EResourceCardType.RANK:
        return (
          <div className={styles.ResourceFeedRankCompWrap}>
            <ResourceFeedRankComp
              data={cardItem.hotRankCardVO}
              trackData={{
                page_name: "首页",
                previous_page_name: previousPageName,
                tab_name: activityTagName, // 当前组件所在tab/分类的名称，枚举值：全部、女装等
                // tab_rank: activityTagIndex + 1, // 当前所在tab/分类的排序，从左至右，1开始
                resource_name: cardItem?.hotRankCardVO?.rankName, // 榜单的名称，例如“连衣裙热销榜”
                resource_type: "榜单",
                comp_name: "商品feed流",
                comp_type: "feed流",
                resource_rank: cardItem.rank, //组件的在整个页中的排序，从1开始；
                comp_index: "", //	组件在横向导航内的相对排序，从1开始,这个取不到
                relative_rank: index + 1, //组件的在整个页中的排序，从1开始；
              }}
            />
          </div>
        );
      default:
        console.error("未知的资源位卡片类型", cardItem.cardType);
        return null;
    }
  };

  // 监听页面code和类目code，设置查询参数并加载数据
  useEffect(() => {
    if (!pageId) {
      console.error("pageId参数必传");
      return;
    }
    // 设置查询参数
    productParamsRef.current = {
      idsClick: clickedSpuId.current.join(),
      lastActivitySpuCode: "",
      pageId,
      pageIndex: 1,
      pageSize: 10,
      scene: "homeFeeds",
      searchRequestId: "",
      industryCategoryId: activityTagNo,
      preView: previewRsc,
    };
    // 重置列表状态
    setFeedList([]);
    totalIndex.current = 0;
    // 加载第一页数据
    loadOnePageFeedList();
  }, [activityTagNo, pageId]);

  // 检查ProductWaterfall组件是否支持customRender属性
  // 如果不支持，需要修改ProductWaterfall组件或使用其他方式处理卡片
  return (
    <div className={styles.waterfall_container}>
      <ProductWaterfall
        productList={feedList}
        hasNext={hasNext}
        wraperStyle={{
          paddingTop: "10px",
          backgroundColor: "#F1F2F5",
          borderRadius: "12px 12px 0 0",
        }}
        loadMore={loadOnePageFeedList}
        trackData={{
          domain: "首页",
          module: "甄选精品",
          resource_name: "feed",
          resource_content: "商品",
          resource_type: "feed",
        }}
        btnClickName="resource_click"
        onClickProduct={product => clickedSpuId.current.push(product?.activitySpuId || "")}
        topRightLocationCode="H5_Feed_ProductList_ProductPicRight"
        titleFrontLocationCode="H5_Feed_ProductList_TitleFront"
        titleBottomLocationCode="H5_Feed_ProductList_TitleDown"
        customChildRender={resourceCardRender}
      />
    </div>
  );
};

export default ProductFeedList;
